'use client'
import { useCallback, useEffect, useRef } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { RECAPTCHA_ERROR_CODES, RecaptchaAction } from '@/types/recaptcha'

/**
 * How long to wait for the reCAPTCHA script to finish loading before giving up
 * on a token. `executeRecaptcha` is undefined until the provider's script
 * resolves, and on a slow connection a user can submit before that happens —
 * which used to send an empty token and get the request rejected server-side.
 * Bounded so a blocked script never hangs a purchase indefinitely.
 */
const READY_TIMEOUT_MS = 4000
const READY_POLL_MS = 100

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Whether a fresh reCAPTCHA token could plausibly succeed. The server decides
 * this (`recaptchaRetryable`) and only sets it for an expired or malformed
 * token — never for a low score, which would fail identically on a retry and
 * would hand a bot two attempts per submission.
 *
 * Deliberately not derived from the error message: the server returns the same
 * generic message for every rejection reason, so a bot can't tell why it was
 * blocked.
 */
export const isRetryableRecaptchaError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false

  const { code, recaptchaRetryable } = error as { code?: unknown, recaptchaRetryable?: unknown }

  // The code is checked alongside the flag because `createHandler` spreads
  // upstream error objects into the response: a backend payload that happened
  // to carry `recaptchaRetryable` would otherwise re-issue a purchase that DID
  // reach the gateway. Our gate rejects before the upstream call, so retrying
  // its own errors cannot double-charge.
  return recaptchaRetryable === true && RECAPTCHA_ERROR_CODES.includes(code as number)
}

/**
 * Single place where reCAPTCHA tokens are minted, shared by auth and commerce.
 *
 * Both helpers degrade to an empty token rather than throwing: an empty token
 * is rejected server-side as `missing_token`, which renders the actionable
 * "refresh the page / your browser may be blocking reCAPTCHA" UX. Throwing here
 * instead would surface as an opaque failure with no guidance.
 */
export const useRecaptcha = () => {
  const { executeRecaptcha } = useGoogleReCaptcha()

  // Held in a ref so the async helpers below can observe the script becoming
  // available *after* they started waiting, without re-creating callbacks or
  // reading the ref during render.
  const executeRef = useRef<typeof executeRecaptcha>(undefined)

  useEffect(() => {
    executeRef.current = executeRecaptcha
  }, [executeRecaptcha])

  const getRecaptchaToken = useCallback(async (action: RecaptchaAction) => {
    const deadline = READY_TIMEOUT_MS / READY_POLL_MS

    for (let attempt = 0; attempt < deadline && !executeRef.current; attempt++) {
      await wait(READY_POLL_MS)
    }

    const execute = executeRef.current

    if (!execute) return ''

    try {
      return await execute(action) || ''
    } catch {
      // Google's script can reject (network blip, quota, blocked host). Fall
      // through to the empty-token path so the user gets the refresh prompt.
      return ''
    }
  }, [])

  /**
   * Runs `request` with a fresh token, retrying once if the server says the
   * token itself was the problem.
   *
   * Safe on purchase flows specifically because our gate throws before the
   * request is proxied upstream: a retried call cannot double-charge, since the
   * rejected attempt never reached the payment gateway.
   */
  const withRecaptchaRetry = useCallback(async <TResponse>(
    action: RecaptchaAction,
    request: (recaptchaToken: string) => Promise<TResponse>,
  ) => {
    const recaptchaToken = await getRecaptchaToken(action)

    try {
      return await request(recaptchaToken)
    } catch (error) {
      if (!isRetryableRecaptchaError(error)) throw error

      // An empty first token means the script never became available, and a
      // retry would just burn the ready-wait again before failing the same way.
      // Fail now so a blocked reCAPTCHA shows its "refresh the page" modal
      // promptly instead of after two full waits.
      if (!recaptchaToken) throw error

      return request(await getRecaptchaToken(action))
    }
  }, [getRecaptchaToken])

  return { getRecaptchaToken, withRecaptchaRetry }
}
